import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription, zip } from 'rxjs';
import { map, skip } from 'rxjs/operators';
import { CategoryDTO } from 'src/app/models/Categories/categoryDTO';
import { ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig, TableEvent } from 'src/app/models/custom-table/custom-table.model';
import { PropertyDTO } from 'src/app/models/Properties/propertyDTO';
import { SiteDTO } from 'src/app/models/Sites/siteDTO';
import { getColorByBgColor, getColorFromStr } from 'src/app/shared/utils/colorUtils';
import { setCategories, setColumnInfoCategoriesProperties, setProperties } from 'src/app/shared/utils/tableUtils';
import { getCategoriesForListByUsage, getPropertiesForListByUsage, getSitesList, getSitesTableConfig, saveSitesTableConfig } from 'src/app/store/actions/sites/sites.actions';
import { AppState, loadedCategoriesListSiteState, loadedCustomersTableConfig, loadedPropertiesListSiteState, sitesStateIsLoading, sitesStateSitesList, sitesStateTotalSites } from 'src/app/store/reducers';

const USAGE_CODE = "SEARCH_SITE";

@Component({
  selector: 'app-search-site-dialog',
  templateUrl: './search-site-dialog.component.html',
  styleUrls: ['./search-site-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class SearchSiteDialogComponent implements OnInit, OnDestroy {
  public isLoading$: Observable<boolean>;
  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public siteList$: Observable<SiteDTO[]>;
  public siteList: SiteDTO[];
  public totalSites$: Observable<number>;
  public selectedRow: object = {};

  private siteListSub: Subscription;
  private multiSubs: Subscription;

  private categories$: Observable<CategoryDTO[]>;
  private properties$: Observable<PropertyDTO[]>;

  public tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.selectSite",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "SiteManagement.Code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "SiteManagement.SiteNumber",
      field: "siteNumber",
      fieldType: FieldType.STRING,
      searchable: true,
      style: "font-weight: 600",
    },
    {
      header: "SiteManagement.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.RegionDescription",
      field: "region.description",
      fieldType: FieldType.CHIP_STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.Profile",
      field: "siteProfile.description",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "SiteManagement.Enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "SiteManagement.Street",
      field: "street",
      fieldType: FieldType.STRING,
    },
    {
      header: "SiteManagement.City",
      field: "city",
      fieldType: FieldType.STRING,
    },
    {
      header: "SiteManagement.PostalZipCode",
      field: "postalZipCode",
      fieldType: FieldType.STRING,
    },
    {
      header: "SiteGroup.siteGroup",
      field: "siteGroup.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "SiteManagement.nDispensers",
      field: "siteDevices.nDispensers",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "SiteManagement.nFccs",
      field: "siteDevices.nFccs",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "SiteManagement.nFuelPoints",
      field: "siteDevices.nFuelPoints",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "SiteManagement.nPos",
      field: "siteDevices.nPos",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "SiteManagement.nWarehouses",
      field: "siteDevices.nWarehouses",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "SiteManagement.Created",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "SiteManagement.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "SiteManagement.comments",
      field: "comment",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  constructor(
    private dialogRef: MatDialogRef<SearchSiteDialogComponent>,
    private store: Store<AppState>
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
    this.siteList$ = this.store.pipe(select(sitesStateSitesList));
    this.totalSites$ = this.store.pipe(select(sitesStateTotalSites));
    this.categories$ = this.store.pipe(select(loadedCategoriesListSiteState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListSiteState));
  }

  ngOnInit(): void {
    this.columnInfo$ = this.store.pipe(select(loadedCustomersTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
        }
      })
    );
    this.getSitesCategoriesProperties(USAGE_CODE, this.getSitesList.bind(this));
  }

  public onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getSitesList.bind(this));
  }

  private getTableData(tableEvent: TableEvent, getSites: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getSites(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  private getSitesList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getSitesList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  public onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.store.dispatch(
      saveSitesTableConfig({
        config: {
          code: USAGE_CODE,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }

  getSitesCategoriesProperties(usageCode: string, getSites: Function) {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories.length) {
        const tableConfigCategories = setCategories(categories);

        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties.length) {
        const tableConfigProperties = setProperties(properties);

        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig(usageCode);
      getSites();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  private setTableCustomElements(): void {
    this.siteListSub = this.siteList$
      .pipe(skip(1))
      .subscribe((sites: any[]) => {
        this.siteList = sites;

        if (sites) {
          this.siteList = this.siteList.map((site) => {
            let processedSite = site;
            processedSite = this.setCustomSiteDetails(processedSite);
            return processedSite;
          });
        }
      });
  }

  private setCustomSiteDetails(siteDetail: SiteDTO) {
    const customSiteDetail = {
      ...siteDetail,
    };

    var regionDescriptionData = null;
    if (siteDetail?.region?.description) {
      regionDescriptionData = {
        value: siteDetail.region.description,
        icon: "place",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.region?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.region?.code) +
          `;`,
      };
    }

    var siteProfileDescriptionData = null;
    if (siteDetail?.siteProfile?.description) {
      siteProfileDescriptionData = {
        value: siteDetail.siteProfile.description,
        //icon: "local_gas_station",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.siteProfile?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.siteProfile?.code) +
          `;`,
      };
    }

    return {
      ...customSiteDetail,
      region: {
        ...customSiteDetail.region,
        description: regionDescriptionData,
      },
      siteProfile: {
        ...customSiteDetail.siteProfile,
        description: siteProfileDescriptionData,
      },
    };
  }

  private getTableConfig(usageCode: string): void {
    this.store.dispatch(
      getSitesTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  public setSelectedRow(event): void {
    const { row, dblClick } = event;
    this.selectedRow = row;
    if (dblClick) {
      this.onConfirmClick();
    }
  }

  public isSelectedRowEmpty(): boolean {
    return Object.keys(this.selectedRow).length === 0;
  }

  public onConfirmClick(): void {
    this.dialogRef.close(this.selectedRow);
  }

  ngOnDestroy(): void {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.siteListSub) {
      this.siteListSub.unsubscribe();
    }
  }
}